// src/modules/operaciones/inventarios/components/ModalDetalleAjuste.js
//
// Modal a pantalla completa con el detalle de un ajuste (vista tabla / tarjetas) y sus botones.
// Se renderiza con createPortal a document.body → nunca hereda el scroll de contenedores padre.
import React from "react"; // Importa React para usar JSX
import { createPortal } from "react-dom"; // Renderiza el modal fuera del árbol padre
import { fmt, fmtFecha } from "../../../../utils/format"; // Números y fechas compartidos
import { useModalA11y } from "../../../../hooks/useModalA11y"; // Scroll lock, Esc, focus trap y retorno de foco
import { BTN_PRIMARY, txt, num, esIdentificador, fmtValor } from "../utils/constantes"; // Constantes y helpers
import { cantidadLinea, infoPrecio } from "../utils/reglasPrecio"; // Reglas de precio
import { COLUMNAS_OCULTAS_DETALLE } from "../utils/exportarExcel"; // Columnas del detalle que no se muestran
import Badge from "./Badge"; // Etiqueta pequeña de color

// Tarjeta etiqueta/valor usada en el encabezado del modal
const Campo = ({ label, mono, className = "", tema, children }) => (
  // Contenedor de la tarjeta
  <div className={`${tema.cardBg} ${tema.cardBorde} backdrop-blur-md rounded-2xl border shadow-sm p-4 ${className}`}>
    {/* Etiqueta en mayúsculas */}
    <p className={`${tema.textoSuave} font-bold text-[10px] uppercase tracking-wide`}>{label}</p>
    {/* Valor, en fuente monoespaciada si mono es true */}
    <p className={`${tema.textoFuerte} font-medium mt-1 text-xs ${mono ? "font-mono" : ""}`}>{children}</p>
  </div>
);

/* ---- Modal de detalle ---- */

// Etiqueta ámbar que muestra el precio calculado y su origen
const EtiquetaPrecio = ({ pc, clase }) => (
  // El tooltip muestra el detalle del cálculo
  <span className={clase} title={pc.detalle}>
    🧮 {fmtValor("AJDE_COSTO", pc.precio)} · {pc.origen}
  </span>
);

// Vista de tabla del detalle del ajuste
const DetalleTabla = ({ lineas, columnas, precios, tema }) => (
  // Contenedor con borde redondeado
  <div className={`rounded-2xl border ${tema.bordeSuave} overflow-hidden`}>
    {/* Tabla con texto pequeño */}
    <table className={`w-full text-left text-[11px] ${tema.textoNormal}`}>
      {/* Encabezado fijo al hacer scroll */}
      <thead className={`${tema.thead} sticky top-0 border-b ${tema.bordeInterno} shadow-sm z-10 backdrop-blur`}>
        <tr>
          {/* Una columna por cada campo visible */}
          {columnas.map((col) => (
            <th key={col} className="px-4 py-3 font-bold whitespace-nowrap">
              {col}
            </th>
          ))}
        </tr>
      </thead>
      <tbody>
        {/* Recorre las líneas; la expresión interna calcula precio/alerta */}
        {lineas.map((fila, i) => {
          const { pc, alerta } = infoPrecio(fila, precios); // Precio calculado o alerta de esta línea
          return (
            // Fila en rojo si hay alerta; normal en caso contrario
            <tr
              key={i}
              className={`border-b transition-colors ${
                alerta ? "bg-red-200 border-l-4 border-l-red-600 border-b-red-300 hover:bg-red-300" : `${tema.bordeFila} ${tema.hoverFila}`
              }`}
            >
              {/* Una celda por columna */}
              {columnas.map((col, idx) => (
                // Números (no ids) alineados a la derecha en monoespaciada
                <td
                  key={col}
                  className={`px-4 py-2.5 whitespace-nowrap ${
                    typeof fila[col] === "number" && !esIdentificador(col) ? "text-right font-mono" : ""
                  }`}
                >
                  {/* Icono de alerta en la primera celda */}
                  {idx === 0 && alerta && <span className="mr-1">⚠️</span>}
                  {/* Valor formateado de la celda */}
                  {fmtValor(col, fila[col])}
                  {/* Etiqueta con el precio calculado junto al costo */}
                  {col === "AJDE_COSTO" && pc && (
                    <EtiquetaPrecio pc={pc} clase="ml-1 text-[10px] bg-amber-500/20 text-amber-800 px-1 rounded" />
                  )}
                  {/* Chip rojo "REVISAR" junto al costo cuando hay alerta */}
                  {col === "AJDE_COSTO" && alerta && (
                    <span
                      className="ml-1 text-[10px] bg-red-600 text-white px-2 py-0.5 rounded-full font-bold animate-pulse"
                      title={alerta.detalle}
                    >
                      ⚠️ REVISAR
                    </span>
                  )}
                </td>
              ))}
            </tr>
          );
        })}
      </tbody>
    </table>
  </div>
);

// Vista de tarjetas del detalle del ajuste
const DetalleTarjetas = ({ lineas, precios, tema }) => (
  // Grilla responsive de tarjetas
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
    {/* Una tarjeta por línea de detalle */}
    {lineas.map((fila, i) => {
      const { pc, alerta } = infoPrecio(fila, precios); // Precio calculado o alerta de la línea
      const cant = cantidadLinea(fila); // Cantidad ajustada de la línea
      return (
        // Tarjeta roja pulsante si hay alerta
        <div
          key={i}
          className={`rounded-2xl p-4 border transition-all hover:shadow-md ${
            alerta ? "bg-red-100 border-red-400 inv-pulse-slow" : `${tema.inputBg} ${tema.bordeSuave}`
          }`}
        >
          {/* Cabecera: número de ítem, descripción y badge de alerta */}
          <div className="flex justify-between items-start gap-2 mb-2">
            <p className={`font-bold text-xs truncate ${tema.textoFuerte}`} title={txt(fila.AJDE_DESC)}>
              #{txt(fila.AJUS_ITEM) || i + 1} {txt(fila.AJDE_DESC) || "—"}
            </p>
            {alerta && (
              <Badge tema={tema} tono="red" pulse>
                ⚠️
              </Badge>
            )}
          </div>
          {/* Cuadrícula con código, bodega, existencia y costo */}
          <div className={`grid grid-cols-2 gap-2 text-[10px] ${tema.textoNormal}`}>
            {/* Código del artículo */}
            <div>
              <span className={tema.textoMuted}>Código:</span> <span className="font-mono">{txt(fila.ARTI_COD) || "—"}</span>
            </div>
            {/* Bodega de la línea */}
            <div>
              <span className={tema.textoMuted}>Bodega:</span> <span className="font-mono">{txt(fila.BODE_COD) || "—"}</span>
            </div>
            {/* Existencia antes → después, con diferencia coloreada */}
            <div className="col-span-2">
              <span className={tema.textoMuted}>Existencia:</span> <span className="font-mono">{fmt(num(fila.AJUS_EXANT), 3)}</span> →{" "}
              <span className="font-mono font-bold">{fmt(num(fila.AJUS_EXNUE), 3)}</span>{" "}
              <span className={`font-bold ${cant > 0 ? "text-emerald-600" : cant < 0 ? "text-red-600" : tema.textoMuted}`}>
                ({cant > 0 ? "+" : ""}
                {fmt(cant, 3)})
              </span>
            </div>
            {/* Costo: alerta de revisión o valor con precio calculado */}
            <div className={`col-span-2 mt-1 pt-2 border-t ${tema.bordeSuave}`}>
              <span className={tema.textoMuted}>Costo:</span>{" "}
              {alerta ? (
                <span className="font-bold text-red-700">⚠️ REVISAR (Costo 0)</span>
              ) : (
                <>
                  <span className="font-mono">{fmtValor("AJDE_COSTO", fila.AJDE_COSTO)}</span>
                  {pc && <EtiquetaPrecio pc={pc} clase="ml-2 text-[10px] bg-amber-500/20 text-amber-800 px-1.5 py-0.5 rounded font-bold" />}
                </>
              )}
            </div>
          </div>
        </div>
      );
    })}
  </div>
);

// Modal a pantalla completa con el detalle de un ajuste
const ModalDetalle = ({ ajuste, lineas, precios, tema, vista, setVista, onClose, onExportar, visto, esPermanente, onTogglePermanente }) => {
  const { modalRef, inicialRef } = useModalA11y(onClose); // Scroll lock, Esc, focus trap y retorno de foco

  const columnas = lineas.length > 0 ? Object.keys(lineas[0]).filter((c) => !COLUMNAS_OCULTAS_DETALLE.has(c)) : []; // Columnas visibles tomadas de la primera línea
  const productosConAlerta = new Set(lineas.filter((d) => infoPrecio(d, precios).alerta).map((d) => txt(d.ARTI_COD))).size; // Cantidad de productos únicos con alerta

  // Datos del encabezado: [etiqueta, valor, ¿monoespaciado?]
  const campos = [
    ["Fecha", fmtFecha(ajuste.AJUS_FECHA)], // Fecha del ajuste
    ["Prefijo", txt(ajuste.PREF_PRE) || "—"], // Prefijo
    ["Bodega", txt(ajuste.BODE_COD) || "—"], // Bodega
    ["Número", txt(ajuste.AJUS_NUMERO) || "—", true], // Número (monoespaciado)
    ["Usuario", txt(ajuste.AJUS_USUARIO) || "—"], // Usuario
    ["Estado", txt(ajuste.AJUS_ANULADO) === "S" ? "Anulado" : "Activo"], // Estado legible
  ];

  // Genera un botón del selector Tabla/Tarjetas
  const botonVista = (id, label) => (
    // Azul si es la vista activa
    <button
      onClick={() => setVista(id)}
      className={`px-3 py-1.5 text-[11px] font-bold transition-colors ${
        vista === id ? "bg-[#007aff] text-white" : `${tema.inputBg} ${tema.textoNormal}`
      }`}
    >
      {label}
    </button>
  );

  // Renderiza el modal en document.body mediante portal
  return createPortal(
    // Fondo oscuro desenfocado; clic fuera cierra el modal
    <div
      ref={modalRef}
      className="fixed inset-0 z-[100] bg-black/70 backdrop-blur-xl inv-fade-in"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label={`Detalle del ajuste ${ajuste.AJUS_ID}`}
    >
      {/* Panel del modal; stopPropagation evita cerrar al hacer clic dentro */}
      <div className={`${tema.modalBg} w-full h-full overflow-y-auto inv-scale-in`} onClick={(e) => e.stopPropagation()}>
        {/* Barra superior fija con título y controles */}
        <div className={`sticky top-0 z-30 w-full ${tema.modalBg} backdrop-blur-xl border-b ${tema.bordeInterno} shadow-md`}>
          <div className="max-w-[1600px] mx-auto px-4 sm:px-8 py-4 flex justify-between items-center gap-4 flex-wrap">
            {/* Lado izquierdo: icono y título */}
            <div className="flex items-center gap-3 flex-wrap">
              {/* Icono del modal */}
              <div
                className={`w-11 h-11 rounded-2xl ${tema.inputBg} border ${tema.bordeSuave} flex items-center justify-center text-xl shadow-sm shrink-0`}
              >
                📋
              </div>
              {/* Título y badge de visto */}
              <div>
                <h3 className={`text-lg sm:text-xl font-extrabold tracking-tight ${tema.textoFuerte} leading-tight`}>
                  Detalle del Ajuste #{ajuste.AJUS_ID}
                </h3>
                {visto && (
                  <div className="mt-1">
                    <Badge tema={tema} tono={esPermanente ? "green" : "gray"}>
                      {esPermanente ? "✅ Revisado permanente" : "👁️ Observado en esta sesión"}
                    </Badge>
                  </div>
                )}
              </div>
            </div>
            {/* Lado derecho: selector de vista y botón cerrar */}
            <div className="flex items-center gap-2">
              {/* Selector Tabla/Tarjetas */}
              <div className={`flex rounded-xl border ${tema.bordeSuave} overflow-hidden`}>
                {botonVista("tabla", "📋 Tabla")}
                {botonVista("tarjetas", "🃏 Tarjetas")}
              </div>
              {/* Botón cerrar con foco inicial */}
              <button
                ref={inicialRef}
                onClick={onClose}
                aria-label="Cerrar detalle"
                className={`px-3 py-2 rounded-xl ${tema.btnBg} ${tema.btnTexto} text-xs font-bold border ${tema.btnBorde} cursor-pointer`}
                title="Cerrar (Esc)"
              >
                ✕ Cerrar
              </button>
            </div>
          </div>
        </div>

        {/* Contenido principal del modal */}
        <div className="max-w-[1600px] mx-auto px-4 sm:px-8 py-6">
          {/* Cuadrícula de campos del ajuste */}
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6 text-xs">
            {/* Una tarjeta por cada campo */}
            {campos.map(([label, valor, mono]) => (
              <Campo key={label} label={label} mono={mono} tema={tema}>
                {valor}
              </Campo>
            ))}
            {/* Concepto ocupa todo el ancho */}
            <Campo label="Concepto" className="col-span-2 md:col-span-3 lg:col-span-6" tema={tema}>
              {txt(ajuste.AJUS_CONC) || "—"}
            </Campo>
          </div>

          {/* Si no hay líneas muestra estado vacío; si hay, el detalle */}
          {lineas.length === 0 ? (
            <div className={`py-10 flex flex-col items-center gap-2 ${tema.textoSuave}`}>
              <div className="text-4xl">📂</div>
              <p className="text-sm font-bold">Este ajuste no tiene líneas de detalle</p>
            </div>
          ) : (
            <>
              {/* Contador de líneas */}
              <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
                <p className={`text-xs font-bold ${tema.textoNormal}`}>
                  {fmt(lineas.length, 3)} {lineas.length === 1 ? "línea" : "líneas"} de detalle
                </p>
              </div>

              {/* Aviso rojo si hay productos con costo promedio 0 */}
              {productosConAlerta > 0 && (
                <div className="mb-3 p-3 bg-red-100 border-2 border-red-400 rounded-xl text-xs font-bold text-red-900 flex items-center gap-2">
                  ⚠️ {fmt(productosConAlerta, 3)} {productosConAlerta === 1 ? "producto" : "productos"} con alerta: Costo
                  Promedio 0 (posible existencia negativa). Revisar antes de exportar.
                </div>
              )}

              {/* Alterna entre vista tabla y vista tarjetas */}
              {vista === "tabla" ? (
                <DetalleTabla lineas={lineas} columnas={columnas} precios={precios} tema={tema} />
              ) : (
                <DetalleTarjetas lineas={lineas} precios={precios} tema={tema} />
              )}
            </>
          )}

          {/* Botonera inferior del modal */}
          <div className="flex justify-end mt-6 gap-2 flex-wrap">
            {/* Botón para marcar/quitar revisado permanente */}
            <button
              onClick={onTogglePermanente}
              aria-pressed={esPermanente}
              className={
                esPermanente
                  ? "px-4 py-2 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-bold shadow-sm cursor-pointer"
                  : `px-4 py-2 rounded-xl ${tema.btnBg} ${tema.btnTexto} text-xs font-bold border ${tema.btnBorde} shadow-sm cursor-pointer`
              }
            >
              {esPermanente ? "✅ Revisado (quitar)" : "Marcar como revisado"}
            </button>
            {/* Botón exportar este ajuste a Excel */}
            <button
              onClick={() => onExportar(ajuste)}
              className="px-4 py-2 rounded-xl bg-sky-500 hover:bg-sky-600 text-white text-xs font-bold shadow-sm cursor-pointer"
            >
              📊 Exportar este ajuste
            </button>
            {/* Botón cerrar del pie */}
            <button onClick={onClose} className={BTN_PRIMARY}>
              Cerrar
            </button>
          </div>
        </div>
      </div>
    </div>,
    document.body // Destino del portal: el body del documento
  );
};

export default ModalDetalle;